import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminGetOrders, adminUpdateOrderStatus } from "@/lib/adminApi";

const fmt = (n) => "€" + Number(n || 0).toFixed(2).replace(".", ",");

const statusPill = (s) => {
  if (s === "paid") return <span className="pill green">Payée</span>;
  if (s === "expired") return <span className="pill terra">Expirée</span>;
  return <span className="pill gray">En attente</span>;
};

const FULFILLMENT_LABELS = {
  en_preparation: "En préparation",
  expediee: "Expédiée",
  livree: "Livrée",
};

const fulfillmentPill = (s) => {
  if (s === "expediee") return <span className="pill green">Expédiée</span>;
  if (s === "livree") return <span className="pill green">Livrée</span>;
  return <span className="pill gray">En préparation</span>;
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ fulfillment_status: "en_preparation", tracking_number: "", carrier: "" });
  const [saving, setSaving] = useState(false);

  const load = () => adminGetOrders().then(setOrders).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openOrder = (o) => {
    setOpen(o.id);
    setForm({
      fulfillment_status: o.fulfillment_status || "en_preparation",
      tracking_number: o.tracking_number || "",
      carrier: o.carrier || "",
    });
  };

  const saveStatus = async (orderId) => {
    setSaving(true);
    try {
      const updated = await adminUpdateOrderStatus(orderId, form);
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...updated } : o)));
      toast.success(
        form.fulfillment_status === "expediee"
          ? "Commande marquée expédiée — email « Votre colis est en route » envoyé au client."
          : "Statut de la commande mis à jour."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour du statut.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="admin-title" style={{ marginBottom: "1.5rem" }}>Commandes</h1>
      {loading ? <div className="adm-loader"><div className="spinner" /></div> : (
        <table className="admin-table" data-testid="orders-table">
          <thead><tr><th>Date</th><th>Client</th><th>Articles</th><th>Total</th><th>Paiement</th><th>Préparation</th><th></th></tr></thead>
          <tbody>
            {orders.length === 0 ? <tr><td colSpan="7" className="adm-hint" style={{ padding: "2rem", textAlign: "center" }}>Aucune commande pour l'instant.</td></tr> : null}
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`}>
                <td>{o.created_at ? new Date(o.created_at).toLocaleString("fr-FR") : "—"}</td>
                <td>{o.customer?.first_name} {o.customer?.last_name}<br /><span className="adm-hint">{o.customer?.email}</span></td>
                <td>{(o.items || []).reduce((s, i) => s + i.quantity, 0)}</td>
                <td><strong>{fmt(o.amount)}</strong></td>
                <td>{statusPill(o.payment_status)}</td>
                <td>{o.payment_status === "paid" ? fulfillmentPill(o.fulfillment_status) : "—"}</td>
                <td><button className="adm-btn ghost sm" onClick={() => openOrder(o)} data-testid={`order-detail-btn-${o.id}`}>Détail</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open ? (() => {
        const o = orders.find((x) => x.id === open);
        if (!o) return null;
        return (
          <div className="adm-overlay" onClick={() => setOpen(null)}>
            <div className="adm-drawer" onClick={(e) => e.stopPropagation()} style={{ width: 460 }} data-testid="order-detail">
              <h2>Commande</h2>
              <p className="adm-hint" style={{ marginBottom: "1rem" }}>{o.id}</p>
              <p style={{ marginBottom: "1rem" }}><strong>{o.customer?.first_name} {o.customer?.last_name}</strong><br />{o.customer?.email}<br />{o.customer?.address}, {o.customer?.postal_code} {o.customer?.city}, {o.customer?.country}</p>
              <table className="admin-table" style={{ marginBottom: "1rem" }}>
                <tbody>
                  {(o.items || []).map((i) => (
                    <tr key={i.product_id}><td>{i.name} × {i.quantity}</td><td style={{ textAlign: "right" }}>{fmt(i.line_total)}</td></tr>
                  ))}
                  {o.discount ? <tr><td>Remise {o.promo_code}</td><td style={{ textAlign: "right" }}>-{fmt(o.discount)}</td></tr> : null}
                  <tr><td>Livraison</td><td style={{ textAlign: "right" }}>{o.shipping === 0 ? "Offerte" : fmt(o.shipping)}</td></tr>
                  <tr><td><strong>Total</strong></td><td style={{ textAlign: "right" }}><strong>{fmt(o.amount)}</strong></td></tr>
                </tbody>
              </table>
              <p style={{ marginBottom: "1rem" }}>Statut paiement : {statusPill(o.payment_status)}</p>

              {o.payment_status === "paid" ? (
                <div data-testid="order-fulfillment-section" style={{ borderTop: "1px solid var(--sand-d)", paddingTop: "1rem" }}>
                  <div className="adm-field">
                    <label>Statut de préparation</label>
                    <select
                      value={form.fulfillment_status}
                      onChange={(e) => setForm((f) => ({ ...f, fulfillment_status: e.target.value }))}
                      data-testid="order-status-select"
                    >
                      {Object.entries(FULFILLMENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>

                  {form.fulfillment_status === "expediee" ? (
                    <div style={{ marginTop: "0.75rem", display: "grid", gap: "0.75rem" }}>
                      <div className="adm-field">
                        <label>Transporteur</label>
                        <input
                          placeholder="ex. bpost, Mondial Relay"
                          value={form.carrier}
                          onChange={(e) => setForm((f) => ({ ...f, carrier: e.target.value }))}
                          data-testid="order-carrier-input"
                        />
                      </div>
                      <div className="adm-field">
                        <label>N° de suivi (optionnel)</label>
                        <input
                          placeholder="N° de suivi"
                          value={form.tracking_number}
                          onChange={(e) => setForm((f) => ({ ...f, tracking_number: e.target.value }))}
                          data-testid="order-tracking-input"
                        />
                      </div>
                      <p className="adm-hint">Un email « Votre colis est en route » sera envoyé au client lors de l'enregistrement.</p>
                    </div>
                  ) : null}

                  <div className="adm-drawer-actions">
                    <button className="adm-btn ghost" onClick={() => setOpen(null)}>Fermer</button>
                    <button className="adm-btn" onClick={() => saveStatus(o.id)} disabled={saving} data-testid="order-status-save">
                      {saving ? "Enregistrement…" : "Enregistrer le statut"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="adm-drawer-actions"><button className="adm-btn ghost" onClick={() => setOpen(null)}>Fermer</button></div>
              )}
            </div>
          </div>
        );
      })() : null}
    </div>
  );
}
